import { inject, Injectable } from '@angular/core';
import { flattenObject } from '@rd/utils/object.utils';
import { schemaValidatorWrapper } from '@rd/utils/schema-validation.utils';
import { z } from 'zod';
import { ZodType } from 'zod/lib/types';
import { EnvService } from '../env/env.service';

export interface IApiValidationService {
  validate<ResponseType>(params: {
    apiName: string;
    schema: ZodType<any>;
    response: ResponseType;
  }): void;
}

// Creating a simple service to use for Local and Non-Production environments
@Injectable({
  providedIn: 'root',
})
export class ApiValidationService implements IApiValidationService {
  validate<ResponseType>(params: {
    apiName: string;
    schema: z.ZodType<any, z.ZodTypeDef, any>;
    response: ResponseType;
  }): void {
    const { apiName, schema, response } = params;
    schemaValidatorWrapper(() => {
      try {
        schema.parse(response);
      } catch (zodError) {
        if (zodError instanceof z.ZodError) {
          const errorLabel = `ZodError: Schema validation for "${apiName}" api`;
          console.log(errorLabel, flattenObject(zodError.format()));
        }
      }
    });
  }
}

// Production validator. Schema errors are no longer reported (Zod is being
// phased out).
@Injectable()
export class ApiValidationWithAuthService implements IApiValidationService {
  private envService = inject(EnvService);

  validate<ResponseType>(params: {
    apiName: string;
    schema: ZodType<any>;
    response: ResponseType;
  }): void {
    if (this.envService.isProduction) {
      return;
    }

    const { schema, response } = params;

    // TODO: remove this schema validation entirely once Zod is fully retired.
    // The parse below currently runs only for a result that is then discarded.
    schemaValidatorWrapper(() => {
      try {
        schema.parse(response);
      } catch {
        // Validation failures are intentionally ignored. Previously reported
        // via rollbarService.zodError(...); removed in the Rollbar sunset and
        // not ported to Sentry.
      }
    });
  }
}

// factory to use at the module level
export function apiValidationFactory(
  production: boolean,
): IApiValidationService {
  return production
    ? new ApiValidationWithAuthService()
    : new ApiValidationService();
}
